import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO, { createWebPageSchema, createBreadcrumbSchema, createOrganizationSchema } from '../../components/feature/SEO';
import WhatsAppButton from '../../components/base/WhatsAppButton';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '../../utils/siteSettings';

const ABOUT_HERO_DEFAULT = 'https://public.readdy.ai/ai/img_res/6de9371414341cbfe64471f6aaf040a0.jpg';

const About = () => {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://unqcode.com';

  const values = [
    { icon: 'ri-lightbulb-flash-line', titleKey: 'about.val1Title', descKey: 'about.val1Desc' },
    { icon: 'ri-shield-check-line', titleKey: 'about.val2Title', descKey: 'about.val2Desc' },
    { icon: 'ri-rocket-line', titleKey: 'about.val3Title', descKey: 'about.val3Desc' },
    { icon: 'ri-team-line', titleKey: 'about.val4Title', descKey: 'about.val4Desc' },
    { icon: 'ri-global-line', titleKey: 'about.val5Title', descKey: 'about.val5Desc' },
    { icon: 'ri-leaf-line', titleKey: 'about.val6Title', descKey: 'about.val6Desc' },
  ];

  const milestones = [
    { yearKey: 'about.ms1Year', titleKey: 'about.ms1Title', descKey: 'about.ms1Desc' },
    { yearKey: 'about.ms2Year', titleKey: 'about.ms2Title', descKey: 'about.ms2Desc' },
    { yearKey: 'about.ms3Year', titleKey: 'about.ms3Title', descKey: 'about.ms3Desc' },
    { yearKey: 'about.ms4Year', titleKey: 'about.ms4Title', descKey: 'about.ms4Desc' },
    { yearKey: 'about.ms5Year', titleKey: 'about.ms5Title', descKey: 'about.ms5Desc' },
    { yearKey: 'about.ms6Year', titleKey: 'about.ms6Title', descKey: 'about.ms6Desc' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO
        title={t('seo.aboutTitle')}
        description={t('seo.aboutDesc')}
        keywords="about Unique Code, about us, company history, digital agency Saudi Arabia, tech company Riyadh, من نحن, عن الشركة"
        canonical={`${siteUrl}/about`}
        ogType="website"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            createWebPageSchema(
              t('seo.aboutTitle', 'About Unique Code | Digital Agency – Riyadh, KSA'),
              t('seo.aboutDesc', 'Learn about Unique Code, a leading digital solutions agency in Saudi Arabia. Our mission, vision, values, and journey in transforming businesses through technology.'),
              '/about'
            ),
            createOrganizationSchema(),
            createBreadcrumbSchema([
              { name: 'Home', url: '/' },
              { name: 'About Us', url: '/about' },
            ]),
            {
              '@type': 'AboutPage',
              '@id': `${siteUrl}/about#aboutpage`,
              name: 'About Unique Code',
              description: 'Learn about Unique Code digital agency — our mission, vision, values, and journey building digital solutions across Saudi Arabia and the GCC.',
              url: `${siteUrl}/about`,
              mainEntity: {
                '@id': `${siteUrl}/#organization`,
              },
              inLanguage: ['en', 'ar'],
            },
          ],
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center bg-black overflow-hidden">
        <div className="absolute inset-0">
          <img src={getImageUrl('hero-about', ABOUT_HERO_DEFAULT)} alt="" width={1280} height={600} className="w-full h-full object-cover object-top opacity-40" fetchPriority="high" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/60"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-32">
          <div className="max-w-2xl">
            <p className="text-brand-red font-bold text-sm uppercase tracking-widest mb-4">{t('about.heroLabel')}</p>
            <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6">
              {t('about.heroTitle1')}
              <span className="block text-brand-red">{t('about.heroTitle2')}</span>
            </h1>
            <p className="text-white/70 text-lg leading-relaxed">{t('about.heroDesc')}</p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-brand-red font-semibold text-sm uppercase tracking-widest mb-3">{t('about.purposeLabel')}</p>
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-8">{t('about.purposeTitle')}</h2>
              <div className="space-y-8">
                {[
                  { icon: 'ri-focus-3-line', bg: 'bg-brand-red', titleKey: 'about.missionTitle', descKey: 'about.missionDesc' },
                  { icon: 'ri-eye-line', bg: 'bg-gray-900', titleKey: 'about.visionTitle', descKey: 'about.visionDesc' },
                  { icon: 'ri-heart-pulse-line', bg: 'bg-gray-100', iconColor: 'text-brand-red', titleKey: 'about.promiseTitle', descKey: 'about.promiseDesc' },
                ].map((item) => (
                  <div key={item.titleKey} className="flex gap-5">
                    <div className={`w-12 h-12 flex items-center justify-center ${item.bg} rounded-xl flex-shrink-0`}>
                      <i className={`${item.icon} ${item.iconColor || 'text-white'} text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg mb-2">{t(item.titleKey)}</h3>
                      <p className="text-gray-500 leading-relaxed">{t(item.descKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden h-[500px]">
                <img
                  src="https://readdy.ai/api/search-image?query=diverse%20tech%20company%20team%20collaborating%20enthusiastically%20in%20a%20modern%20dark-themed%20office%20space%20with%20large%20monitors%20and%20technology%20equipment%2C%20group%20of%20young%20Arab%20professionals%20working%20together%20around%20a%20table%2C%20innovative%20startup%20atmosphere%20with%20red%20accent%20lighting%2C%20candid%20corporate%20photography%20with%20natural%20expressions&width=560&height=560&seq=about-mission002&orientation=squarish"
                  alt="Our team"
                  width={560}
                  height={560}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 rtl:-left-auto rtl:-right-6 bg-brand-red text-white rounded-2xl p-5 shadow-xl">
                <div className="text-4xl font-black">{t('about.yearsCard')}</div>
                <div className="text-white/80 text-sm mt-1">{t('about.yearsLabel')}</div>
              </div>
              <div className="absolute -top-6 -right-6 rtl:-right-auto rtl:-left-6 bg-gray-900 text-white rounded-2xl p-5 shadow-xl">
                <div className="text-4xl font-black">{t('about.satisfactionCard')}</div>
                <div className="text-white/70 text-sm mt-1">{t('about.satisfactionLabel')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-brand-red font-semibold text-sm uppercase tracking-widest mb-3">{t('about.valuesLabel')}</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900">{t('about.valuesTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((v) => (
              <div key={v.titleKey} className="bg-white rounded-2xl p-7 border border-gray-100 hover:border-brand-red/30 hover:-translate-y-1 transition-all duration-300 group">
                <div className="w-12 h-12 flex items-center justify-center bg-brand-red/10 group-hover:bg-brand-red rounded-xl mb-5 transition-colors duration-300">
                  <i className={`${v.icon} text-brand-red group-hover:text-white text-xl transition-colors duration-300`}></i>
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{t(v.titleKey)}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{t(v.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-brand-red font-semibold text-sm uppercase tracking-widest mb-3">{t('about.timelineLabel')}</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900">{t('about.timelineTitle')}</h2>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 -translate-x-0.5 top-0 bottom-0 w-0.5 bg-gray-200 hidden lg:block"></div>
            <div className="space-y-10">
              {milestones.map((m, i) => (
                <div key={m.yearKey} className={`relative flex flex-col lg:flex-row gap-8 items-start lg:items-center ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={`flex-1 ${i % 2 === 1 ? 'lg:text-right' : ''}`}>
                    <div className={`bg-white border border-gray-100 rounded-2xl p-7 hover:border-brand-red/20 transition-colors duration-300 ${i % 2 === 1 ? 'lg:ml-16' : 'lg:mr-16'}`}>
                      <div className="text-brand-red font-black text-3xl mb-2">{t(m.yearKey)}</div>
                      <h3 className="font-bold text-gray-900 text-xl mb-2">{t(m.titleKey)}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{t(m.descKey)}</p>
                    </div>
                  </div>
                  <div className="hidden lg:flex w-10 h-10 items-center justify-center bg-brand-red rounded-full border-4 border-white shadow-md flex-shrink-0 z-10 absolute left-1/2 -translate-x-1/2">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <div className="flex-1 hidden lg:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Strip */}
      <section className="py-20 bg-brand-red">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-black text-white">{t('about.ctaTitle')}</h2>
            <p className="text-white/80 mt-2">{t('about.ctaDesc')}</p>
          </div>
          <div className="flex gap-4 flex-shrink-0">
            <Link to="/contact">
              <button className="px-8 py-4 bg-white text-brand-red font-bold rounded-xl hover:bg-gray-50 transition-all duration-200 whitespace-nowrap cursor-pointer">{t('about.ctaBtn1')}</button>
            </Link>
            <Link to="/join">
              <button className="px-8 py-4 border-2 border-white/40 text-white font-bold rounded-xl hover:bg-white/10 transition-all duration-200 whitespace-nowrap cursor-pointer">{t('about.ctaBtn2')}</button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default About;
